import { PropsWithChildren, useEffect, useMemo, useReducer } from 'react';

import createSafeContext from '@paradigm/utils/createSafeContext';

import {
  clearValues,
  clearField,
  selectValues,
  reducer,
  hidrate,
  updateMinNotional,
  updateMaxNotional,
  updateCounterparties,
} from './form-store';
import {
  Field,
  FormView,
  getInitialFormState,
  PersistedSelectedFilters,
} from './form-view';
import { useTabsView } from './tabs-context';

interface FormActions {
  readonly clearValues: () => void;
  readonly clearField: (field: Field) => void;
  selectValues<T extends Field>(
    field: T,
    values: FormView[T]['selectedOptions'],
  ): void;
  updateCounterparties<T extends Field>(
    field: T,
    options: FormView[T]['options'],
  ): void;
  hidrate: (filters: PersistedSelectedFilters | null) => void;
  updateMinNotional: (value: string) => void;
  updateMaxNotional: (value: string) => void;
}

export const [useRfqFiltersFormView, FormViewProvider] =
  createSafeContext<FormView>('RfqFiltersFormView');
export const [useRfqFiltersFormActions, FormActionsProvider] =
  createSafeContext<FormActions>('RfqFiltersFormActions');

export default function RfqFilterFormProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialFormState);
  const { activeTab } = useTabsView();

  const actions: FormActions = useMemo(
    () => ({
      clearValues: () => {
        dispatch(clearValues());
      },
      clearField: (field: Field) => {
        dispatch(clearField(field));
      },
      updateMinNotional: (value: string) => {
        dispatch(updateMinNotional(value));
      },
      updateMaxNotional: (value: string) => {
        dispatch(updateMaxNotional(value));
      },
      selectValues: (field, values) => {
        dispatch(selectValues({ field, values }));
      },
      updateCounterparties: (field, options) => {
        dispatch(updateCounterparties({ field, options }));
      },
      hidrate: (filters: PersistedSelectedFilters | null) => {
        dispatch(hidrate(filters));
      },
    }),
    [dispatch],
  );

  useEffect(() => {
    actions.hidrate(activeTab.filter);
  }, [actions, activeTab]);

  return (
    <FormViewProvider value={state}>
      <FormActionsProvider value={actions}>{children}</FormActionsProvider>
    </FormViewProvider>
  );
}
